import { ref, computed } from 'vue'
import type { Guide } from '@/models/Guide'
import type { RoutePoint } from '@/models/RoutePoint'

// Global state
const guides = ref<Guide[]>([])
const currentGuide = ref<Guide | null>(null)
const routePoints = ref<RoutePoint[]>([])
const chosenGuideId = ref<string | null>(null)

// Function to load mock guides based on guide location and language
const loadMockGuides = async (language: string = 'en', guide: string = 'stavanger'): Promise<Guide[]> => {
  try {
    // Dynamic import based on guide and language
    const module = await import(`@/services/guideInfo/${guide}/${language}`)
    return module.default || module
  } catch (error) {
    console.warn(`Failed to load mock guides for ${guide}/${language}, falling back to stavanger/en`)
    try {
      // Fallback to stavanger/en
      const fallbackModule = await import('@/services/guideInfo/stavanger/en')
      return fallbackModule.default || fallbackModule
    } catch (fallbackError) {
      console.error('Error loading fallback mock guides:', fallbackError)
      return []
    }
  }
}

// Legacy mock data (keeping as fallback)
const mockGuides: Guide[] = [];
 

// Computed properties
const currentRoutePoints = computed(() => {
  const points = currentGuide.value?.routePoints || []
  return points.sort((a, b) => a.order - b.order)
})

const chosenGuide = computed(() => {
  if (!chosenGuideId.value) return null
  return guides.value.find(guide => guide.id === chosenGuideId.value) || null
})

// Service methods
export const GuideService = {
  // Initialize with mock data based on language
  async initialize(language: string = 'en', guide: string = 'stavanger') {
    try {
      console.log(`🔄 Loading guides for language: ${language}`)
      const loadedGuides = await loadMockGuides(language, guide)
      console.log(`📦 Loaded guides:`, loadedGuides)
      if (loadedGuides.length > 0) {
        guides.value = loadedGuides
        currentGuide.value = guides.value[0]
        routePoints.value = currentGuide.value.routePoints
        console.log(`✅ Loaded ${loadedGuides.length} guides for language: ${language}`)
      } else {
       throw new Error(`No guides found for language: ${language}`)
      }
    } catch (error) {
      console.error('Error initializing guides:', error)
      guides.value = []
      currentGuide.value = null
      routePoints.value = []
    }
  },

  // Reload guides with new language
  async reloadGuides(language: string, guide: string = 'stavanger') {
    console.log(`🔄 Reloading guides for language: ${language}`)
    await this.initialize(language, guide)
  },

  // Get all guides
  getAllGuides() {
    return guides.value
  },

  // Get current guide
  getCurrentGuide() {
    return currentGuide.value
  },

  // Get current route points
  getCurrentRoutePoints() {
    return currentRoutePoints.value
  },

  // Set current guide
  setCurrentGuide(guideId: string) {
    const guide = guides.value.find(g => g.id === guideId)
    if (guide) {
      currentGuide.value = guide
      routePoints.value = guide.routePoints
    }
  },

  // Choose a guide (for profile selection)
  chooseGuide(guideId: string) {
    chosenGuideId.value = guideId
    localStorage.setItem('chosen-guide-id', guideId)
  },

  // Get chosen guide
  getChosenGuide() {
    return chosenGuide.value
  },

  // Load chosen guide from localStorage
  loadChosenGuide() {
    const savedGuideId = localStorage.getItem('chosen-guide-id')
    if (savedGuideId) {
      chosenGuideId.value = savedGuideId
    }
  },

  // Get available guide locations
  getAvailableGuideLocations() {
    return [
      { id: 'stavanger', name: 'Stavanger', description: 'Stavanger City Tour' },
      { id: 'kotor', name: 'Kotor', description: 'Kotor City Tour' }
    ]
  },

  // Future API methods
  async fetchGuides() {
    // TODO: Implement API call to fetch guides from server
    // const response = await fetch('/api/guides')
    // guides.value = await response.json()
    console.log('Fetching guides from API...')
  }
}

// Export reactive state for direct access if needed
export { guides, currentGuide, routePoints, currentRoutePoints, chosenGuideId, chosenGuide }
